// directives/lazyLoad.js
/**
 * 通用懒加载指令 - 支持图片和视频
 * 使用方式：
 * 图片: <img v-lazy="imageUrl" />
 * 视频: <video v-lazy="{ src: videoUrl, type: 'video' }" controls></video>
 */

// 默认配置
const defaultOptions = {
  // 提前加载的距离
  rootMargin: '200px 0px',
  // 触发阈值
  threshold: 0.01,
  // 加载中的图片
  loadingImage: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODAiIGhlaWdodD0iODAiIHZpZXdCb3g9IjAgMCA1MCA1MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48c3R5bGU+LmxvYWRlcl9jaXJjbGUge3N0cm9rZTogdXJsKCdncmFkaWVudCcpO3N0cm9rZS1saW5lY2FwOiByb3VuZDthbmltYXRpb246IGRhc2ggMS41cyBlYXNlLWluLW91dCBpbmZpbml0ZSwgY29sb3IgNnMgZWFzZS1pbi1vdXQgaW5maW5pdGUsc3Ryb2tlLXdpZHRoOiA0O3N0cm9rZS1kYXNoYXJyYXk6IDEsIDIwMDtzdHJva2UtZGFzaG9mZnNldDogMDtmaWxsOiBub25lO31Aa2V5ZnJhbWVzIGRhc2ggezAlIHtzdHJva2UtZGFzaGFycmF5OiAxLCAyMDA7c3Ryb2tlLWRhc2hvZmZzZXQ6IDA7fSA1MCUge3N0cm9rZS1kYXNoYXJyYXk6IDg5LCAyMDA7c3Ryb2tlLWRhc2hvZmZzZXQ6IC0zNXB4O30gMTAwJSB7c3Ryb2tlLWRhc2hhcnJheTogODksIDIwMDtzdHJva2UtZGFzaG9mZnNldDogLTEyNHB4O319QGtleWZyYW1lcyBjb2xvciB7MCUsIDEwMCUge3N0cm9rZTogIzQyODVmNDt9IDQwJSB7c3Ryb2tlOiAjMzRiODcwO30gNjYlIHtzdHJva2U6ICNmM2NlMDA7fSA4MCUsIDkwJSB7c3Ryb2tlOiAjZjU2MjgyO319LmxvYWRlciB7YW5pbWF0aW9uOiByb3RhdGUgMnMgbGluZWFyIGluZmluaXRlO308L3N0eWxlPjxkZWZzPjxyYWRpYWxHcmFkaWVudCBpZD0iZ3JhZGllbnQiIGN4PSIyNSUiIGN5PSIyNSUiIHI9IjcwJSI+PHN0b3Agb2Zmc2V0PSIwJSIgc3RvcC1jb2xvcj0iIzQyODVmNCIvPjxzdG9wIG9mZnNldD0iMjUlIiBzdG9wLWNvbG9yPSIjMzRiODcwIi8+PHN0b3Agb2Zmc2V0PSI1MCUiIHN0b3AtY29sb3I9IiNmM2NlMDAiLz48c3RvcCBvZmZzZXQ9Ijc1JSIgc3RvcC1jb2xvcj0iI2Y1NjI4MiIvPjxzdG9wIG9mZnNldD0iMTAwJSIgc3RvcC1jb2xvcj0iIzQyODVmNCIvPjwvcmFkaWFsR3JhZGllbnQ+PC9kZWZzPjxnIGNsYXNzPSJsb2FkZXIiPjxjaXJjbGUgY2xhc3M9ImxvYWRlcl9jaXJjbGUiIGN4PSIyNSIgY3k9IjI1IiByPSIyMCIvPjwvZz48L3N2Zz4=',
  // 加载失败的图片
  errorImage: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjBmMGYwIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNiIgZmlsbD0iIzMzMyIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkVycm9yPC90ZXh0Pjwvc3ZnPg',
  // 重试次数
  maxAttempts: 3,
  // 重试间隔(ms)
  retryDelay: 1000
};

// 全局 IntersectionObserver 实例池（优化性能）
const observerPool = new Map();

// 获取或创建 Observer 实例
function getObserver(options = {}) {
  const key = JSON.stringify(options);
  
  if (!observerPool.has(key)) {
    // 创建新的 Observer 实例
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const element = entry.target;
          const state = element._lazyLoadState;
          
          if (state && !state.loaded && !state.loading) {
            loadElement(element);
            observer.unobserve(element);
          }
        }
      });
    }, {
      root: options.root || null,
      rootMargin: options.rootMargin || defaultOptions.rootMargin,
      threshold: options.threshold || defaultOptions.threshold
    });
    
    observerPool.set(key, observer);
  }
  
  return observerPool.get(key);
}

// 加载元素
async function loadElement(element) {
  const state = element._lazyLoadState;
  if (!state || state.loaded || state.loading) return;
  
  state.loading = true;
  element.classList.add('lazy-loading');
  element.classList.remove('lazy-error');
  
  try {
    if (element.tagName === 'IMG') {
      await loadImage(element, state);
    } else if (element.tagName === 'VIDEO') {
      await loadVideo(element, state);
    }
    
    state.loaded = true;
    state.loading = false;
    element.classList.remove('lazy-loading');
    element.classList.add('lazy-loaded');
    
    // 触发自定义事件
    element.dispatchEvent(new CustomEvent('lazy-loaded', {
      detail: { element, type: element.tagName.toLowerCase() }
    }));
    
  } catch (error) {
    state.loading = false;
    element.classList.remove('lazy-loading');
    element.classList.add('lazy-error');
    
    // 重试逻辑
    if (state.attempts < state.maxAttempts) {
      state.attempts++;
      setTimeout(() => loadElement(element), state.retryDelay);
    } else {
      // 触发错误事件
      element.dispatchEvent(new CustomEvent('lazy-error', {
        detail: { element, error, type: element.tagName.toLowerCase() }
      }));
    }
  }
}

// 加载图片
function loadImage(element, state) {
  return new Promise((resolve, reject) => {
    if (!state.src) {
      reject(new Error('No source provided for image'));
      return;
    }
    
    const img = new Image();
    
    img.onload = () => {
      element.src = state.src;
      element.onload = null;
      element.onerror = null;
      resolve();
    };
    
    img.onerror = (error) => {
      element.onload = null;
      element.onerror = null;
      
      // 尝试设置错误图片
      if (state.errorImage) {
        element.src = state.errorImage;
      }
      
      reject(error);
    };
    
    img.src = state.src;
  });
}

// 加载视频
function loadVideo(element, state) {
  return new Promise((resolve, reject) => {
    if (!state.src) {
      reject(new Error('No source provided for video'));
      return;
    }
    
    // 设置视频源
    if (state.srcType === 'source') {
      // 如果使用 <source> 标签
      const source = element.querySelector('source');
      if (source) {
        source.src = state.src;
      }
    } else {
      // 直接设置 video 的 src
      element.src = state.src;
    }
    
    // 加载视频
    element.load();
    
    // 监听视频加载事件
    const onLoaded = () => {
      cleanup();
      resolve();
    };
    
    const onError = (e) => {
      cleanup();
      reject(e);
    };
    
    // 清理函数
    const cleanup = () => {
      element.removeEventListener('loadeddata', onLoaded);
      element.removeEventListener('error', onError);
    };
    
    element.addEventListener('loadeddata', onLoaded, { once: true });
    element.addEventListener('error', onError, { once: true });
    
    // 设置超时
    setTimeout(() => {
      if (!state.loaded) {
        cleanup();
        reject(new Error('Video load timeout'));
      }
    }, 10000); // 10秒超时
  });
}

// 设置元素初始状态
function setupElement(element, binding) {
  const options = binding.value || {};
  const isVideo = element.tagName === 'VIDEO';
  
  // 解析配置
  let src, srcType;
  
  if (typeof options === 'string') {
    // 字符串形式: v-lazy="url"
    src = options;
    srcType = isVideo ? 'video' : 'image';
  } else if (options && typeof options === 'object') {
    // 对象形式: v-lazy="{ src: url, type: 'video' }"
    src = options.src;
    srcType = options.type || (isVideo ? 'video' : 'image');
  }
  
  // 存储状态
  element._lazyLoadState = {
    src,
    srcType,
    loaded: false,
    loading: false,
    attempts: 0,
    maxAttempts: options.maxAttempts || defaultOptions.maxAttempts,
    retryDelay: options.retryDelay || defaultOptions.retryDelay,
    loadingImage: options.loadingImage || defaultOptions.loadingImage,
    errorImage: options.errorImage || defaultOptions.errorImage
  };
  
  // 设置初始状态
  if (element.tagName === 'IMG') {
    // 图片：设置占位图
    if (!element.hasAttribute('src')) {
      element.src = element._lazyLoadState.loadingImage;
    }
    
    // 添加 data-src 属性（用于SEO和降级）
    element.setAttribute('data-src', src);
  } else if (element.tagName === 'VIDEO') {
    // 视频：设置 preload="none"
    element.setAttribute('preload', 'none');
    
    // 设置 poster 占位图（如果未设置）
    if (!element.hasAttribute('poster') && element._lazyLoadState.loadingImage) {
      element.setAttribute('poster', element._lazyLoadState.loadingImage);
    }
    
    // 添加 data-src 属性
    element.setAttribute('data-src', src);
  }
  
  // 添加 CSS 类
  element.classList.add('lazy-element');
}

// 清理元素
function cleanupElement(element) {
  if (element._lazyLoadObserver) {
    element._lazyLoadObserver.unobserve(element);
    delete element._lazyLoadObserver;
  }
  
  delete element._lazyLoadState;
  element.classList.remove('lazy-element', 'lazy-loading', 'lazy-loaded', 'lazy-error');
}

export default {
  name: 'lazy',
  
  // 指令绑定
  bind(el, binding) {
    // 检查是否支持 Intersection Observer
    if (!('IntersectionObserver' in window)) {
      // 不支持：直接加载
      handleFallback(el, binding);
      return;
    }
    
    setupElement(el, binding);
  },
  
  // 元素插入到 DOM
  inserted(el, binding) {
    if (!('IntersectionObserver' in window) || el._lazyLoadState?.loaded) {
      return;
    }
    
    const observerOptions = binding.modifiers || {};
    const observer = getObserver(observerOptions);
    
    // 保存 observer 引用
    el._lazyLoadObserver = observer;
    
    // 开始观察
    observer.observe(el);
    
    // 如果元素在视口内，立即加载
    const rect = el.getBoundingClientRect();
    const windowHeight = window.innerHeight || document.documentElement.clientHeight;
    const windowWidth = window.innerWidth || document.documentElement.clientWidth;
    
    if (
      rect.top <= windowHeight + 200 &&
      rect.bottom >= -200 &&
      rect.left <= windowWidth + 200 &&
      rect.right >= -200
    ) {
      loadElement(el);
    }
  },
  
  // 更新绑定值
  update(el, binding) {
    if (binding.value !== binding.oldValue) {
      // 清理旧状态
      cleanupElement(el);
      
      // 设置新状态
      setupElement(el, binding);
      
      // 重新观察
      if (el._lazyLoadObserver) {
        el._lazyLoadObserver.observe(el);
      }
    }
  },
  
  // 指令解绑
  unbind(el) {
    cleanupElement(el);
  }
};

// 降级处理：直接加载
function handleFallback(el, binding) {
  const options = binding.value || {};
  let src;
  
  if (typeof options === 'string') {
    src = options;
  } else if (options && typeof options === 'object') {
    src = options.src;
  }
  
  if (!src) return;
  
  if (el.tagName === 'IMG') {
    el.src = src;
  } else if (el.tagName === 'VIDEO') {
    if (options.srcType === 'source') {
      const source = el.querySelector('source');
      if (source) {
        source.src = src;
      }
    } else {
      el.src = src;
    }
    el.load();
  }
  
  el.classList.add('lazy-loaded', 'lazy-fallback');
}